import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { PiArrowCounterClockwise } from 'react-icons/pi';

import { isAuthEnabled } from '#/config/auth';
import useTable from '#/hooks/table/useTable';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { TableProvider } from '#/modules/commonComponents/table/TableContext';
import Table from '#/modules/commonComponents/table/Table';
import TablePagination from '#/modules/commonComponents/table/TablePagination';
import TablePageSizeSelect from '#/modules/commonComponents/table/TablePageSizeSelect';
import TableSearchInput from '#/modules/commonComponents/table/TableSearchInput';
import ListFilterStatus from '#/modules/commonComponents/table/ListFilterStatus';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';
const EMPTY_PAGINATION = {
  current_page: 1,
  page_size: 10,
  total_count: 0,
  total_page: 1,
};

function resolveTablePrefsUserId(user) {
  if (user?.id != null && user.id !== '') {
    return String(user.id);
  }
  if (user?.account) {
    return String(user.account);
  }
  if (!isAuthEnabled) {
    return 'anonymous';
  }
  return null;
}

const ListTablePanel = memo(
  forwardRef(({
    title,
    columns,
    rows = [],
    pagination = EMPTY_PAGINATION,
    isLoading = false,
    isError = false,
    errorMessage = null,
    onRetry = null,
    onPageChange,
    onPageSizeChange,
    onRowClick,
    onRowDoubleClick = null,
    headerAction = null,
    toolbarAfterSearch = null,
    searchPlaceholder = '搜尋',
    initialKeyword = '',
    onSearch,
    onReset = null,
    tableClassName = '',
    textSize,
    listQuery = null,
    filterStatusFields = null,
    sortFieldLabels = null,
    onFilterChange = null,
    onKeywordClear = null,
    onSortRemove = null,
    onSortingChange = null,
    enableMultiSelection = false,
    paginationClassName = null,
    initialRowSelection = null,
    onRowSelectionChange = null,
    variant = 'card',
    panelClassName = '',
    serverSide = true,
    initialPageSize,
    tableKey = null,
    enableColumnOrder: enableColumnOrderProp,
    onColumnOrderChange = null,
  }, ref) => {
    const [keyword, setKeyword] = useState(initialKeyword);
    const infraUser = useSelector((state) => state.infra?.user);
    const prefsUserId = resolveTablePrefsUserId(infraUser);
    const enableColumnOrder = enableColumnOrderProp ?? Boolean(tableKey);

    const handlePageChange = useCallback((page) => {
      if (onPageChange) {
        onPageChange(page);
      }
    }, [onPageChange]);

    const handlePageSizeChange = useCallback((pageSize) => {
      if (onPageSizeChange) {
        onPageSizeChange(pageSize);
      }
    }, [onPageSizeChange]);

    const handleSortingChange = useCallback((nextSorting) => {
      if (onSortingChange) {
        onSortingChange(nextSorting);
      }
    }, [onSortingChange]);

    const tableState = useTable(rows, columns, pagination, {
      isLoading,
      onPageChange: handlePageChange,
      onPageSizeChange: handlePageSizeChange,
      onSortingChange: onSortingChange ? handleSortingChange : undefined,
      onRowSelectionChange,
      onColumnOrderChange,
      disableGlobalFilter: true,
      enableRowSelection: true,
      enableMultiSelection,
      serverSide,
      enableColumnOrder,
      tableKey,
      userId: prefsUserId,
      ...(initialPageSize != null ? { initialPageSize } : {}),
      ...(initialRowSelection ? { initialRowSelection } : {}),
    });

    const { setSorting, clearSelection, getSelectedRows, resetAll } = tableState;

    const listSortKey = JSON.stringify(listQuery?.sort ?? []);

    useEffect(() => {
      if (!listQuery) {
        return;
      }

      setSorting(JSON.parse(listSortKey));
    }, [listQuery, listSortKey, setSorting]);

    const clearSelectionRef = useRef(clearSelection);
    clearSelectionRef.current = clearSelection;
    const getSelectedRowsRef = useRef(getSelectedRows);
    getSelectedRowsRef.current = getSelectedRows;

    useImperativeHandle(
      ref,
      () => ({
        clearSelection: () => {
          clearSelectionRef.current?.();
        },
        getSelectedRows: () => getSelectedRowsRef.current?.() ?? [],
      }),
      [],
    );

    const showTitle = Boolean(title);
    const isCompactToolbar = !title;

    const handleReset = useCallback(() => {
      setKeyword('');
      resetAll();
      if (onReset) {
        onReset();
        return;
      }
      if (onSearch) {
        onSearch('');
      }
    }, [onReset, onSearch, resetAll]);

    const panelHeader = useMemo(() => (
      <div
        className={cn(
          'flex shrink-0 flex-col gap-2.5',
          isCompactToolbar
            ? (variant === 'plain' ? 'px-0 pt-0 pb-2' : 'px-5 pt-2 pb-3')
            : 'px-5 pt-2.5 pb-4',
        )}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          {showTitle ? (
            <h2 className="page_title mb-0! shrink-0">{title}</h2>
          ) : null}
          <div
            className={cn(
              'flex min-w-0 flex-wrap items-center justify-between gap-2.5',
              showTitle ? 'flex-1' : 'w-full',
            )}
          >
            <div className="flex min-w-0 flex-wrap items-center gap-2.5">
              {onSearch ? (
                <>
                  <TableSearchInput
                    useKeyword
                    keyword={keyword}
                    onKeywordChange={setKeyword}
                    onSearch={() => {
                      onSearch(keyword);
                    }}
                    placeholder={searchPlaceholder}
                    className="min-w-48 w-full max-w-md sm:w-56"
                  />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="secondary"
                        size="icon"
                        className="size-10 shrink-0"
                        aria-label="清除搜尋與篩選"
                        onClick={handleReset}
                      >
                        <PiArrowCounterClockwise size={20} aria-hidden />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">清除搜尋與篩選</TooltipContent>
                  </Tooltip>
                  {toolbarAfterSearch}
                </>
              ) : null}
              {listQuery && filterStatusFields ? (
                <ListFilterStatus
                  listQuery={listQuery}
                  filterFields={filterStatusFields}
                  sortFieldLabels={sortFieldLabels}
                  onFilterChange={onFilterChange}
                  onKeywordClear={onKeywordClear}
                  onSortRemove={onSortRemove}
                />
              ) : null}
            </div>
            <div className="ml-auto flex shrink-0 flex-wrap items-center gap-2.5">
              <TablePageSizeSelect />
              {headerAction}
            </div>
          </div>
        </div>
      </div>
    ), [
      isCompactToolbar,
      variant,
      showTitle,
      title,
      headerAction,
      toolbarAfterSearch,
      keyword,
      onSearch,
      searchPlaceholder,
      handleReset,
      listQuery,
      filterStatusFields,
      sortFieldLabels,
      onFilterChange,
      onKeywordClear,
      onSortRemove,
    ]);

    return (
      <TableProvider value={tableState}>
        <div
          className={cn(
            'list-table-panel flex h-full min-h-0 flex-col bg-panel',
            variant === 'card' && 'rounded-md border border-gray-200',
            panelClassName,
          )}
        >
          {panelHeader}
          <div className="list-table-panel__body flex min-h-0 flex-1 flex-col bg-panel">
            {isError ? (
              <div className="flex min-h-40 flex-1 flex-col items-center justify-center gap-3 px-5 py-8">
                <p className="text-center text-sm text-rose-700">
                  {errorMessage || '載入列表失敗，請稍後再試'}
                </p>
                {onRetry ? (
                  <Button type="button" variant="outline" onClick={onRetry}>
                    重試
                  </Button>
                ) : null}
              </div>
            ) : (
              <>
                <Table
                  loading={isLoading}
                  rowFn={onRowClick}
                  onRowDoubleClick={onRowDoubleClick}
                  tableClassName={tableClassName}
                  textSize={textSize}
                  containerClassName="list-table-panel__scroll"
                />
                <TablePagination containerClassName={paginationClassName} />
              </>
            )}
          </div>
        </div>
      </TableProvider>
    );
  }),
);

ListTablePanel.propTypes = {
  title: PropTypes.string,
  columns: PropTypes.array.isRequired,
  rows: PropTypes.array,
  pagination: PropTypes.shape({
    current_page: PropTypes.number,
    page_size: PropTypes.number,
    total_count: PropTypes.number,
    total_page: PropTypes.number,
  }),
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  errorMessage: PropTypes.string,
  onRetry: PropTypes.func,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  onRowClick: PropTypes.func,
  onRowDoubleClick: PropTypes.func,
  headerAction: PropTypes.node,
  toolbarAfterSearch: PropTypes.node,
  searchPlaceholder: PropTypes.string,
  initialKeyword: PropTypes.string,
  onSearch: PropTypes.func,
  onReset: PropTypes.func,
  tableClassName: PropTypes.string,
  textSize: PropTypes.oneOf(['sm', 'base']),
  listQuery: PropTypes.object,
  filterStatusFields: PropTypes.array,
  sortFieldLabels: PropTypes.object,
  onFilterChange: PropTypes.func,
  onKeywordClear: PropTypes.func,
  onSortRemove: PropTypes.func,
  onSortingChange: PropTypes.func,
  enableMultiSelection: PropTypes.bool,
  paginationClassName: PropTypes.string,
  initialRowSelection: PropTypes.object,
  onRowSelectionChange: PropTypes.func,
  variant: PropTypes.oneOf(['card', 'plain']),
  panelClassName: PropTypes.string,
  serverSide: PropTypes.bool,
  initialPageSize: PropTypes.number,
  tableKey: PropTypes.string,
  enableColumnOrder: PropTypes.bool,
  onColumnOrderChange: PropTypes.func,
};

ListTablePanel.displayName = 'ListTablePanel';

export default ListTablePanel;
